import { Award, MapPin, Clock, QrCode, ShieldCheck } from "lucide-react";
import { motion } from "motion/react";

export function CertificatePreview() {
  return (
    <section id="certificate" className="py-8 md:py-12 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl mb-4 bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
            GEC Certificate Preview
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Blockchain-verified digital certificates for complete transparency
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8">
          {/* Certificate Mockup */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 p-8 rounded-2xl border-2 border-emerald-200 shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-6 border-b border-emerald-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Green Energy Certificate</p>
                    <p className="text-sm">GEC-2025-001234</p>
                  </div>
                </div>
                <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center border border-emerald-200">
                  <QrCode className="w-12 h-12 text-gray-700" />
                </div>
              </div>

              {/* Certificate Details */}
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Consumer Name</p>
                  <p className="text-sm">Green Energy Industries Pvt. Ltd.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Period</p>
                    <p className="text-sm">November 2025</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Total Units</p>
                    <p className="text-sm">15,420 kWh</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-600 mb-1">Energy Source Mix</p>
                  <div className="flex gap-2 mt-2">
                    <div className="flex-1 bg-yellow-400 h-2 rounded"></div>
                    <div className="flex-1 bg-cyan-400 h-2 rounded"></div>
                    <div className="flex-1 bg-blue-400 h-2 rounded"></div>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-600 mt-1">
                    <span>Solar 35%</span>
                    <span>Wind 38%</span>
                    <span>Hydro 27%</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-6 border-t border-emerald-200">
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>Issued: Nov 29, 2025</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>Gujarat, India</span>
                  </div>
                </div>
              </div>

              {/* Verification Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-emerald-500 to-cyan-500 text-white px-4 py-2 rounded-full text-xs shadow-lg">
                ✓ Verified
              </div>
            </div>
          </motion.div>

          {/* Certificate Features */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg mb-2 text-gray-900">Hourly Match Score</h3>
                  <p className="text-gray-600 mb-3">
                    Advanced algorithm matches your consumption with renewable generation every hour.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 h-3 rounded-full" style={{ width: '96%' }}></div>
                    </div>
                    <span className="text-sm">96%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="text-lg mb-2 text-gray-900">Energy Origin Map</h3>
                  <p className="text-gray-600 mb-3">
                    Track the exact location and source of your renewable energy.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white p-2 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-500">Wind Farm</p>
                      <p className="text-xs">Kutch, Gujarat</p>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-500">Solar Park</p>
                      <p className="text-xs">Charanka, Gujarat</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Verify Certificate Button - Spans both columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-2 flex justify-center mt-4"
          >
            <a
              href="/verify"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-emerald-500 text-emerald-600 rounded-xl hover:bg-emerald-50 transition-all shadow-md hover:shadow-lg"
            >
              <ShieldCheck className="w-5 h-5" />
              <span>Verify Certificate</span>
            </a>
            <a
              href="/upload-bills"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-emerald-500 text-emerald-600 rounded-xl hover:bg-emerald-50 transition-all shadow-md hover:shadow-lg"
            >
              <ShieldCheck className="w-5 h-5" />
              <span>Upload Bills</span>
            </a>
          </motion.div>
        </div>


        {/* Verify Certificate Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mt-12"
        >

        </motion.div>
      </div>
    </section>
  );
}
